import { useState } from 'react';
import { nomeAnexo } from '../lib/nomeArquivo';
import { Alert, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { LISTA_ITENS } from '../lib/enviarPlanner';
import { enviarVariosAoPlanner, perguntarEnvio, prefillRenovacao } from '../lib/envioPlanner';   // texto 10 (E1)
import { useDoc } from '../store/store';
import { AcoesItem } from '../components/Acoes';
import { Ordenavel } from '../components/Arrastar';
import { Arquivados, useSelecao } from '../components/Selecao';
import { useOrganizar } from '../lib/organizar';
import { paraLixeira } from '../lib/lixeira';
import { useBotaoNovo } from '../lib/botaoNovo';
import { useNovoComNota } from '../components/NotaRapida';
import type { Anexo, Documento } from '../store/types';
import { OPCOES_AVISO, TIPOS_DOC, alertasDoDoc, avisosPadrao, diasParaVencer, excluirDocumento, iconeDoc, novoDocumento, salvarDocumento, somarAnos } from '../store/documentos';
import { abreAqui, abrirAnexo, apagarAnexo, compartilharAnexo, compartilharTexto, lerBase64, protegerAnexo, temLocal } from '../lib/anexos';
import { iaPronta } from '../lib/ia';
import { lerDocumento } from '../lib/iaUsos';
import { useFonteIA } from '../components/FonteIA';
import { ActivityIndicator } from 'react-native';
import { useAnexar } from '../components/MenuAnexo';
import type { OrigemAnexo } from '../lib/anexar';
import { semBloqueio } from '../auth/AuthContext';
import { useTaskSheet } from '../components/TaskSheet';
import { PickerModal } from '../components/Pickers';
import { AlertasEditor } from '../components/AlertasEditor';
import { diasDosAlertas, opcoesDias } from '../lib/alertasData';
import { DesenhoSheet, DesenhoView } from '../components/Desenho';
import { ReceivedBoxes } from '../components/BoxCard';
import { ActionSheet, Btn, Chip, Field, Screen, Sheet, type Action } from '../components/ui';
import { CampoRico } from '../components/CampoRico';
import { PaginaRolavel } from '../components/PaginaEditor';
import { C, alpha, useCores } from '../components/theme';
import { brYmd, norm, parseYmd, today, ymd, uid } from '../lib/date';
import { t } from '../lib/i18n';
import { cifrar, decifrar, useCofre } from '../lib/cofre';
import { useCofreSheet } from '../components/CofreSheet';
import { useCaixas } from '../lib/caixas';

type Segredo = { numero: string; orgao: string; notas: string };
/** Abre os dados protegidos do documento (o cofre precisa estar aberto). */
function abrirSegredo(d: Documento): Documento | null {
  if (!d.segredo) return d;
  const txt = decifrar(d.segredo);
  if (txt === null) return null;
  try { const x = JSON.parse(txt) as Segredo; return { ...d, numero: x.numero ?? '', orgao: x.orgao ?? '', notas: x.notas ?? '' }; } catch { return null; }
}

type Filtro = 'todos' | 'vencendo' | 'vencidos' | 'sem';
const erro = (e: any) => Alert.alert(t('Não foi possível'), String(e?.message ?? e));
const nomeDoc = (d: Documento) => d.nome.trim() || t(d.tipo);

/** Texto curto para enviar junto ou no lugar do arquivo. */
function resumo(d: Documento) {
  const l = [`${iconeDoc(d.tipo)} ${nomeDoc(d)}`];
  if (d.titular) l.push(t('Titular: {nome}', { nome: d.titular }));
  if (d.numero) l.push(t('Número: {n}', { n: d.numero }));
  if (d.orgao) l.push(t('Órgão emissor: {nome}', { nome: d.orgao }));
  if (d.emissao) l.push(t('Emissão: {data}', { data: brYmd(d.emissao) }));
  l.push(d.validade ? t('Validade: {data}', { data: brYmd(d.validade) }) : t('Sem validade'));
  return l.join('\n');
}

/** Linha de situação: "Vence em 25 dia(s) · até 30/10/2026". */
function situacao(d: Documento) {
  const dl = diasParaVencer(d);
  if (dl === null) return { txt: t('Sem validade'), cor: C.sub };
  const ate = t('até {data}', { data: brYmd(d.validade!) });
  if (dl < 0) return { txt: `${t('Venceu há {n} dia(s)', { n: -dl })} · ${ate}`, cor: C.danger };
  if (dl === 0) return { txt: `${t('Vence hoje')} · ${ate}`, cor: C.danger };
  const limite = Math.max(30, ...d.avisos);
  return { txt: `${t('Vence em {n} dia(s)', { n: dl })} · ${ate}`, cor: dl <= 7 ? C.danger : dl <= limite ? C.warn : C.ok };
}

export default function Documentos({ item, onBack }: { item?: string; onBack: () => void }) {
  const { items } = useDoc('documentos');
  const { boxes } = useDoc('anotacoes');
  const { corDe } = useCores();
  const cor = corDe('documentos');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const sheet = useTaskSheet();
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busca, setBusca] = useState('');
  const [f, setF] = useState<Documento | null>(null);
  const [picker, setPicker] = useState<'emissao' | 'validade' | null>(null);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [link, setLink] = useState('');
  const [desenhar, setDesenhar] = useState(false);
  const [lendo, setLendo] = useState(false);
  const fonteIA = useFonteIA();
  const cofre = useCofre();
  const cofreSheet = useCofreSheet(cor);
  const [protegido, setProtegido] = useState(false);
  // 0.72 (11g): Fotos · Tirar foto · Arquivos pelo mesmo caminho do menu de anexos (protegido: não vai para a nuvem)
  const anexo = useAnexar({ cor, semNuvem: protegido });
  /** Abre a ficha; documento do cofre pede a senha antes. */
  async function editar(d: Documento) {
    if (d.segredo) {
      if (!(await cofreSheet.pedir())) return;
      const aberto = abrirSegredo(d);
      if (!aberto) return Alert.alert(t('Não foi possível abrir'), t('Senha do cofre incorreta.'));
      setProtegido(true); setF(aberto);
    } else { setProtegido(!!cofre.configurado); setF(d); }
  }

  /** Preenche a ficha lendo a foto (ou PDF) com a IA do usuário. */
  async function preencherComIA() {
    if (!f) return;
    if (!(await iaPronta())) return Alert.alert(t('IA não ligada'), t('Ligue a sua IA em Ajustes › Inteligência artificial.'));
    const ja = f.arquivos.find(a => (temLocal(a) || a.cofre) && (a.mime?.startsWith('image/') || a.mime === 'application/pdf'));
    try {
      const r = await fonteIA.escolher(protegido, ja);
      if (!r) return;
      const arq = r.arq;
      if (arq !== ja) setF(x => (x ? { ...x, arquivos: [...x.arquivos, arq] } : x));
      if (arq.cofre && !(await cofreSheet.pedir())) return;
      setLendo(true);
      const dados = await lerDocumento({ mime: arq.mime || 'image/jpeg', base64: await lerBase64(arq) });
      setF(x => {
        if (!x) return x;
        const vazio = (v: unknown) => v === '' || v == null;
        const novo: Documento = { ...x };
        for (const [k, v] of Object.entries(dados)) if (!vazio(v) && vazio((x as any)[k])) (novo as any)[k] = v;
        if (dados.tipo && dados.tipo !== x.tipo) { novo.tipo = dados.tipo; novo.avisos = avisosPadrao(dados.tipo); }
        return novo;
      });
      Alert.alert(t('Ficha preenchida'), t('Confira os campos antes de salvar.'));
    } catch (e) { erro(e); }
    finally { setLendo(false); }
  }
  const novo = f && !items.some(x => x.id === f.id);
  const org = useOrganizar('documentos');
  const novoDoc = () => { setProtegido(!!cofre.configurado); setF(novoDocumento()); };
  const novoTela = useNovoComNota('documentos', { rotulo: t('Novo documento'), fn: novoDoc });   // texto 29: + com Nota rápida
  /** Excluir: a ficha vai para a lixeira (30 dias); os arquivos deste aparelho são apagados, então a cópia leva só os que continuam existindo. */
  const excluir = (d: Documento) => {
    paraLixeira('documentos', 'lista', nomeDoc(d), { ...d, arquivos: d.arquivos.filter(a => a.atalho || !(a.uri && abreAqui(a))) }, { doc: 'documentos', campo: 'items' });
    d.arquivos.forEach(apagarAnexo); excluirDocumento(d.id);
  };
  const confirmarExcluir = (d: Documento) => Alert.alert(t('Excluir "{nome}"?', { nome: nomeDoc(d) }), t('Os arquivos guardados neste dispositivo também serão apagados.') + '\n\n' + t('Fica 30 dias na lixeira.'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluir(d) }]);

  const vencendo = (d: Documento) => { const dl = diasParaVencer(d); return dl !== null && dl >= 0 && dl <= Math.max(30, ...d.avisos); };
  const sep = org.separar(items, d => d.id);
  const lista = sep.ativos
    .filter(d => !busca.trim() || norm(`${d.tipo} ${t(d.tipo)} ${d.nome} ${d.titular} ${d.orgao} ${d.notas}`).includes(norm(busca.trim())))
    .filter(d => filtro === 'todos' ? true : filtro === 'sem' ? !d.validade : filtro === 'vencidos' ? (diasParaVencer(d) ?? 0) < 0 : vencendo(d))
    .sort((a, b) => (+org.fixado(b.id) - +org.fixado(a.id)) || (diasParaVencer(a) ?? 1e9) - (diasParaVencer(b) ?? 1e9));   // fixados primeiro
  const sel = useSelecao({ itens: lista, chave: d => d.id, cor, onArquivar: ids => org.arquivar(ids), genero: 'm',
    // texto 21 (B3): selecionar vários e enviar de uma vez (cada um vira o lembrete de renovação)
    extras: [{ label: '📅 ' + t('Enviar para o My Planner'), onPress: ids => enviarVariosAoPlanner(ids.map(x => ({ aba: 'documentos' as const, id: x, titulo: nomeDoc(items.find(d => d.id === x)!) }))) }],
    onExcluir: ids => ids.forEach(id => { const d = items.find(x => x.id === id); if (d) excluir(d); }) });
  const qtd = { vencendo: items.filter(vencendo).length, vencidos: items.filter(d => (diasParaVencer(d) ?? 0) < 0).length };

  async function salvar() {
    if (!f) return;
    if (f.validade && f.emissao && f.validade < f.emissao) return Alert.alert(t('A validade não pode ser antes da emissão.'));
    let doc: Documento = { ...f, nome: f.nome.trim(), titular: f.titular.trim(), numero: f.numero.trim(), orgao: f.orgao.trim(), segredo: null };
    if (protegido) {
      if (!(await cofreSheet.pedir())) return;
      try {
        doc = { ...doc, segredo: cifrar(JSON.stringify({ numero: doc.numero, orgao: doc.orgao, notas: doc.notas })), numero: '', orgao: '', notas: '', notasRico: null };   // 11f H: o editor não fica aberto fora do cofre
        const arquivos: Anexo[] = [];
        for (const a of doc.arquivos) arquivos.push(await protegerAnexo(a));   // fotos e PDFs também vão para o cofre
        doc = { ...doc, arquivos };
      }
      catch (e) { return erro(e); }
    }
    const novo = !items.some(x => x.id === doc.id);
    salvarDocumento(doc);
    setF(null); setLink('');
    // texto 10 (E1): documento novo com validade pergunta se vai para o My Planner (abre o mesmo "Lembrete de renovação")
    if (novo && doc.validade) { const d = doc; perguntarEnvio({ aba: 'documentos', id: d.id, titulo: nomeDoc(d), emoji: iconeDoc(d.tipo), data: d.validade, lista: LISTA_ITENS,
      abrir: () => sheet.open({ prefill: prefillRenovacao(d) }) }); }
  }

  async function addArquivo(origem: OrigemAnexo) {
    try {
      const r = await anexo.anexar({ origem, tipo: 'tudo', varios: origem === 'fotos' });
      if (r) setF(x => (x ? { ...x, arquivos: [...x.arquivos, r.anexo, ...(r.outros ?? [])] } : x));
      if (r?.aviso) Alert.alert(t('Arquivo adicionado'), r.aviso);
    } catch (e) { erro(e); }
  }
  function addLink() {
    const u = link.trim();
    if (!/^https:\/\/\S+$/i.test(u)) return Alert.alert(t('O link deve começar com https://'));
    setF(x => (x ? { ...x, arquivos: [...x.arquivos, { nome: t('Link'), url: u }] } : x));
    setLink('');
  }

  /** Abre ou envia um arquivo; os do cofre pedem a senha antes. */
  async function usarArquivo(a: Anexo, fn: (a: Anexo) => Promise<unknown>) {
    if (a.cofre && !(await cofreSheet.pedir())) return;
    try { await semBloqueio(() => fn(a)); } catch (e) { erro(e); }
  }
  const enviarArquivo = (a: Anexo) => usarArquivo(a, compartilharAnexo);
  async function enviarResumo(d0: Documento) {
    let d = d0;
    if (d0.segredo) {
      if (!(await cofreSheet.pedir())) return;
      const x = abrirSegredo(d0);
      if (!x) return;
      d = x;
    }
    try { if ((await semBloqueio(() => compartilharTexto(resumo(d), nomeDoc(d)))) === 'copiado') Alert.alert(t('Texto copiado. Cole onde quiser enviar.')); }
    catch (e) { erro(e); }
  }

  /** Compartilhar com um toque: com um arquivo só, envia direto; com mais, pergunta qual. */
  function compartilhar(d: Documento) {
    const ok = d.arquivos.filter(abreAqui);
    if (ok.length === 1 && d.arquivos.length === 1) return enviarArquivo(ok[0]);
    if (!ok.length) return enviarResumo(d);
    setMenu({ t: t('Enviar "{nome}"', { nome: nomeDoc(d) }), a: [
      ...ok.map(a => ({ label: `${a.cofre ? '🔐' : a.url ? '🔗' : a.mime?.startsWith('image/') ? '🖼️' : '📎'} ${nomeAnexo(a)}`, onPress: () => enviarArquivo(a) })),
      { label: '📝 ' + t('Só os dados (texto)'), onPress: () => enviarResumo(d) },
    ] });
  }

  function opcoes(d: Documento) {
    const a: Action[] = [{ label: '📤 ' + t('Compartilhar'), onPress: () => compartilhar(d) }];
    for (const arq of d.arquivos) if (abreAqui(arq)) a.push({ label: '👁 ' + t('Abrir {nome}', { nome: nomeAnexo(arq) }), onPress: () => usarArquivo(arq, abrirAnexo) });
    if (d.validade) a.push({ label: '📅 ' + t('Enviar para o My Planner (lembrete de renovação)'), onPress: () => sheet.open({ prefill: prefillRenovacao(d) }) });
    a.push({ label: t('Editar'), onPress: () => editar(d) });
    a.push({ label: '📦 ' + t('Arquivar'), onPress: () => org.arquivar(d.id) });
    a.push({ label: t('Excluir'), destructive: true, onPress: () => confirmarExcluir(d) });
    setMenu({ t: nomeDoc(d), a });
  }

  const FILTROS: [Filtro, string][] = [
    ['todos', t('Todos')], ['vencendo', t('Vencendo') + (qtd.vencendo ? ` (${qtd.vencendo})` : '')],
    ['vencidos', t('Vencidos') + (qtd.vencidos ? ` (${qtd.vencidos})` : '')], ['sem', t('Sem validade')],
  ];

  return (
    <Screen title={t('Documentos')} onBack={onBack}>
      <Field value={busca} onChangeText={setBusca} placeholder={t('Buscar documento, titular ou órgão')} />
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {FILTROS.map(([k, l]) => <Chip key={k} small cor={cor} on={filtro === k} label={l} onPress={() => setFiltro(k)} />)}
      </View>
      {!items.length && (
        <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 16, gap: 8 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>🪪 {t('Guarde seus documentos num lugar só')}</Text>
          <Text style={{ color: C.sub, fontSize: 13 }}>{t('Cadastre RG, CNH, passaporte, contratos e certidões. O app avisa antes de vencer e envia o documento com um toque.')}</Text>
          <Btn cor={cor} label={'+ ' + t('Novo documento')} onPress={novoDoc} />
        </View>
      )}
      {/* texto 21 (B1): sem o cartão da lista inteira; o envio é item por item */}
      {!!items.length && !lista.length && <Text style={{ color: C.sub }}>{t('Nenhum documento neste filtro.')}</Text>}

      {sel.barra}
      <Ordenavel itens={lista} chave={d => d.id} gap={cx.gap(10)} desligado onOrdem={() => {}} selecao={sel.prop} render={d => {
        const s = situacao(d);
        return (
          <AcoesItem lembrar={{ aba: 'documentos', id: d.id, titulo: nomeDoc(d) }} raio={cx.raio(16)} nome={nomeDoc(d)} aviso={t('Os arquivos guardados neste dispositivo também serão apagados.')} menuPos="baixo"
            onExcluir={() => excluir(d)} onArquivar={() => org.arquivar(d.id)}
            fixar={{ on: org.fixado(d.id), alternar: () => org.alternarFixo(d.id) }}
            onDuplicar={() => salvarDocumento({ ...d, id: uid(), nome: t('{titulo} (cópia)', { titulo: nomeDoc(d) }), arquivos: [], createdAt: new Date().toISOString() })}
            pacote={() => ({ titulo: nomeDoc(d), emoji: iconeDoc(d.tipo), data: d.validade, origem: 'documentos', texto: d.validade ? t('Validade') + ': ' + brYmd(d.validade) : '' })}>
          <Pressable onPress={() => opcoes(d)} style={{ backgroundColor: C.card, borderRadius: cx.raio(16), ...cx.pads(14), gap: cx.gap(6), borderWidth: 1, borderColor: d.id === item ? cor : C.line }}>
            <View style={{ flexDirection: 'row', gap: cx.gap(12), alignItems: 'center' }}>
              <Text style={{ fontSize: cx.icone(28) }}>{iconeDoc(d.tipo)}</Text>
              <View style={{ flex: 1, gap: 2 }}>
                <Text style={{ color: C.text, fontWeight: '700', fontSize: 16 }} numberOfLines={1}>{nomeDoc(d)}</Text>
                <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{[d.segredo ? '🔐' : '', d.titular, d.nome.trim() ? t(d.tipo) : '', d.orgao].filter(Boolean).join(' · ')}</Text>
              </View>
              <Pressable onPress={() => compartilhar(d)} hitSlop={8} style={{ paddingHorizontal: 12, paddingVertical: cx.pad(8), borderRadius: 12, backgroundColor: alpha(cor, 0.2) }}>
                <Text style={{ color: C.text, fontWeight: '700', fontSize: 12 }}>📤 {t('Enviar')}</Text>
              </Pressable>
            </View>
            <Text style={{ color: s.cor, fontWeight: '700', fontSize: 13 }}>{s.txt}</Text>
            {!!d.arquivos.length && <Text style={{ color: C.mute, fontSize: 12 }}>📎 {t('{n} arquivo(s)', { n: d.arquivos.length })}</Text>}
          </Pressable>
          </AcoesItem>
        );
      }} />
      <Arquivados itens={sep.arquivados} chave={d => d.id} cor={cor} render={d => <Text style={{ color: C.text }} numberOfLines={1}>{iconeDoc(d.tipo)} {nomeDoc(d)}</Text>}
        onRestaurar={d => org.restaurar(d.id)} onExcluir={confirmarExcluir} />

      <ReceivedBoxes boxes={boxes} abaId="documentos" />
      {novoTela}
      <View style={{ height: 90 }} />

      <Sheet visible={!!f} onClose={() => { setF(null); setLink(''); }} title={novo ? t('Novo documento') : t('Editar documento')}
        right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
        {f && (
          <PaginaRolavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 80 }}>
            <Pressable onPress={preencherComIA} disabled={lendo} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, backgroundColor: alpha(cor, 0.15) }}>
              {lendo ? <ActivityIndicator color={cor} /> : <Text style={{ fontSize: 18 }}>🤖</Text>}
              <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>{lendo ? t('Lendo o documento...') : t('Preencher pela foto com a minha IA')}</Text>
            </Pressable>
            <Pressable onPress={() => setProtegido(x => !x)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, backgroundColor: protegido ? alpha(cor, 0.2) : C.card, borderWidth: 1, borderColor: protegido ? cor : C.line }}>
              <Text style={{ fontSize: 18 }}>{protegido ? '🔐' : '🔓'}</Text>
              <View style={{ flex: 1 }}>
                <Text style={{ color: C.text, fontWeight: '700' }}>{protegido ? t('Protegido no cofre') : t('Proteger no cofre')}</Text>
                <Text style={{ color: C.sub, fontSize: 12 }}>{t('Número, órgão emissor, observações, fotos e PDFs ficam criptografados (AES-256) no dispositivo. Os arquivos protegidos ficam só neste dispositivo.')}</Text>
              </View>
            </Pressable>
            <Text style={{ color: C.sub }}>{t('Tipo')}</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              {TIPOS_DOC.map(([tp, ic]) => (
                <Chip key={tp} small cor={cor} on={f.tipo === tp} label={`${ic} ${t(tp)}`}
                  onPress={() => setF({ ...f, tipo: tp, avisos: f.tipo === tp ? f.avisos : avisosPadrao(tp), alertas: f.tipo === tp ? f.alertas : undefined })} />
              ))}
            </View>
            <Field value={f.nome} onChangeText={v => setF({ ...f, nome: v })} placeholder={t('Nome (opcional, ex.: CNH do Walter)')} />
            <Field value={f.titular} onChangeText={v => setF({ ...f, titular: v })} placeholder={t('Titular (de quem é o documento)')} />
            <Field value={f.numero} onChangeText={v => setF({ ...f, numero: v })} placeholder={t('Número (opcional)')} autoCorrect={false} />
            <Field value={f.orgao} onChangeText={v => setF({ ...f, orgao: v })} placeholder={t('Órgão emissor (ex.: DETRAN-MS, Polícia Federal)')} />

            <Text style={{ color: C.sub }}>{t('Emissão')}</Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              <Chip cor={cor} on={false} label={`📅 ${f.emissao ? brYmd(f.emissao) : t('Escolher data')}`} onPress={() => setPicker('emissao')} />
              {!!f.emissao && <Chip cor={cor} on={false} label={t('Limpar')} onPress={() => setF({ ...f, emissao: null })} />}
            </View>

            <Text style={{ color: C.sub }}>{t('Validade')}</Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              <Chip cor={cor} on={!f.validade} label={t('Não vence')} onPress={() => setF({ ...f, validade: null })} />
              <Chip cor={cor} on={!!f.validade} label={`📅 ${f.validade ? brYmd(f.validade) : t('Escolher data')}`} onPress={() => setPicker('validade')} />
            </View>
            {!!f.emissao && (
              <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <Text style={{ color: C.mute, fontSize: 12 }}>{t('Emissão +')}</Text>
                {[1, 2, 3, 5, 10].map(n => <Chip key={n} small cor={cor} on={false} label={t('{n} ano(s)', { n })} onPress={() => setF({ ...f, validade: somarAnos(f.emissao!, n) })} />)}
              </View>
            )}

            {!!f.validade && (<>
              {/* texto 10 (A4): o mesmo editor de alertas da Nova tarefa (dias antes, hora e "Como avisar") */}
              <Text style={{ color: C.sub }}>🔔 {t('Alertas do vencimento')}</Text>
              <AlertasEditor itens={alertasDoDoc(f)} temHorario={false} cor={cor} aba="documentos" dias={opcoesDias(OPCOES_AVISO)}
                onChange={l => setF({ ...f, alertas: l, avisos: diasDosAlertas(l) })} />
            </>)}

            <Text style={{ color: C.sub }}>{t('Arquivos (frente, verso, PDF)')}</Text>
            {f.arquivos.map((a, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: 12, padding: 10 }}>
                <Text style={{ fontSize: 18 }}>{a.cofre ? '🔐' : a.nuvem ? '☁️' : a.url ? '🔗' : a.mime?.startsWith('image/') ? '🖼️' : '📎'}</Text>
                <Pressable style={{ flex: 1 }} onPress={() => usarArquivo(a, abrirAnexo)}>
                  <Text style={{ color: C.text }} numberOfLines={1}>{a.nuvem ? nomeAnexo(a) : a.url ?? nomeAnexo(a)}</Text>
                  {!abreAqui(a) && <Text style={{ color: C.mute, fontSize: 11 }}>{t('Guardado em outro dispositivo')}</Text>}
                </Pressable>
                <Pressable hitSlop={8} onPress={() => { apagarAnexo(a); setF({ ...f, arquivos: f.arquivos.filter((_, j) => j !== i) }); }}><Text style={{ color: C.mute }}>✕</Text></Pressable>
              </View>
            ))}
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              <Chip cor={cor} on={false} label={'🖼️ ' + t('Fotos')} onPress={() => addArquivo('fotos')} />
              {Platform.OS !== 'web' && <Chip cor={cor} on={false} label={'📷 ' + t('Tirar foto')} onPress={() => addArquivo('camera')} />}
              <Chip cor={cor} on={false} label={'📎 ' + t('Arquivos')} onPress={() => addArquivo('arquivos')} />
            </View>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Field value={link} onChangeText={setLink} placeholder={t('Link (Drive, OneDrive, Dropbox)')} autoCapitalize="none" autoCorrect={false} style={{ flex: 1 }} />
              <Chip cor={cor} on={false} label={t('Adicionar')} onPress={addLink} />
            </View>
            <Text style={{ color: C.mute, fontSize: 11 }}>{t('Fotos e arquivos ficam guardados só neste dispositivo. Os dados do documento vão para a sua conta, protegidos.')}</Text>

            <Text style={{ color: C.sub }}>{t('Anotação à mão')}</Text>
            {f.tracos?.length || f.quadro ? (
              <Pressable onPress={() => setDesenhar(true)}><DesenhoView tracos={f.tracos ?? []} alt={f.alt ?? 500} larg={f.larg} quadro={f.quadro} /></Pressable>
            ) : null}
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Chip cor={cor} on={false} label={'✍️ ' + (f.tracos?.length || f.quadro ? t('Editar desenho') : t('Desenhar com caneta ou dedo'))} onPress={() => setDesenhar(true)} />
              {!!(f.tracos?.length || f.quadro) && <Chip cor={cor} on={false} label={t('Apagar desenho')} onPress={() => Alert.alert(t('Apagar o desenho?'), '', [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Apagar'), style: 'destructive', onPress: () => setF({ ...f, tracos: [], quadro: null, alt: undefined, larg: undefined }) }])} />}
            </View>

            {/* 11f H: observações no editor das páginas; documento protegido pelo cofre continua com a caixa simples (o editor não é criptografado) */}
            {protegido ? <Field multiline value={f.notas} onChangeText={v => setF({ ...f, notas: v })} placeholder={t('Observações (onde está o original, categoria da CNH...)')} style={{ minHeight: 80, textAlignVertical: 'top' }} /> : (<>
              <Text style={{ color: C.sub }}>{t('Observações')}</Text>
              <CampoRico valor={f.notas} rico={f.notasRico} cor={cor} aba="documentos" dica={t('Observações (onde está o original, categoria da CNH...)')}
                onChange={(tx, rico) => setF(x => (x ? { ...x, notas: tx, notasRico: rico } : x))} />
            </>)}
            <Btn cor={cor} label={t('Salvar')} onPress={salvar} />
          </PaginaRolavel>
        )}
        {picker && f && <PickerModal mode="date" cor={cor} value={parseYmd((picker === 'emissao' ? f.emissao : f.validade) ?? today())} onClose={() => setPicker(null)}
          onPick={d => setF(x => (x ? { ...x, [picker]: ymd(d) } : x))} />}
        {anexo.tela}
        {cofreSheet.tela}
        {fonteIA.tela}
        <DesenhoSheet visible={desenhar} inicial={f?.tracos} inicialQ={f?.quadro} alt={f?.alt ?? 500} larg={f?.larg} cor={cor} aba="documentos" onClose={() => setDesenhar(false)}
          onSalvar={(tracos, alt, larg, quadro) => { setF(x => (x ? { ...x, tracos, alt, larg, quadro } : x)); setDesenhar(false); }} />
      </Sheet>
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
      {!f && cofreSheet.tela}
    </Screen>
  );
}
